import React, { useState, useEffect, useDeferredValue } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Paper,
  InputBase,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  MoreVert,
  Comment,
  FilterListOutlined,
  SearchOutlined,
  Book,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ViewBook from "./MemoryTableView";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import thumbnailTemplate from "../../../../assets/thumbnailTemplate1.jpeg";
import { IMemorialItem, IColumn } from "../../../../types/memory-table";
import apiService from "../../../../service/Api";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { showErrorToast } from "../../../../components/common/ShowToast";
import { MemoryBookStats } from "./components/MemoryBookStats";
import { TableView } from "./components/TableView";
import { User } from "../../../users-admin/types/User";

interface MemoryBookStreamInfo {
  is_delivered: boolean;
  is_memory_book_available: boolean;
  approved_comments: number;
  total_comments: number;
  canva_memory_book_link: string | null;
  assignees: User[];
  created_at: string;
  last_updated_by: User;
  name_of_deceased: string;
  stream_date: string;
  stream_id: number;
  updated_at: string;
}

// Map API status to column IDs
const statusToColumnMap: { [key: string]: string } = {
  Enquiry: "assembled",
  // Add more mappings as needed, e.g.:
  // Draft: "assembled",
  // InReview: "review",
  // Approved: "approved",
  // Printing: "printing",
};

const getBookID = (status: boolean) => {
  if (status) {
    return "approved";
  } else {
    return "assembled";
  }
};

// Initial columns with an "Other" column for unmapped statuses
const initialColumns: IColumn[] = [
  {
    id: "assembled",
    title: "Book Being Assembled",
    description:
      "This is the first stage, where the book is being designed and prepared before it goes for review.",
    count: 0,
    items: [],
  },
  {
    id: "review",
    title: "Sent for Review & Revisions",
    description:
      "This is the stage where the book is reviewed and revisions are made.",
    count: 0,
    items: [],
  },
  {
    id: "approved",
    title: "Approved, Awaiting Finalization",
    description: "The book has been approved and is awaiting final touches.",
    count: 0,
    items: [],
  },
  {
    id: "printing",
    title: "Ready for Printing & Printing in Progress",
    description: "The book is ready for or currently in the printing process.",
    count: 0,
    items: [],
  },
  {
    id: "other",
    title: "Other Status",
    description: "Streams with statuses not mapped to specific columns.",
    count: 0,
    items: [],
  },
];

// Sortable Card Component
const SortableCard: React.FC<{ item: IMemorialItem }> = ({ item }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to={`/memory-table/view/${item.id}`}
        style={{ textDecoration: "none", color: "inherit", cursor: "pointer" }}
      >
        <Card
          sx={{
            mb: 2,
            cursor: "grab",
            boxShadow: 0,
            backgroundColor: reduxTheme.primary_colors[800],
            borderRadius: 5,
            "&:active": { cursor: "grabbing" },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Chip
                label={item.priority}
                size="small"
                color={item.priority === "High" ? "warning" : "default"}
                sx={{ fontSize: "0.75rem" }}
              />
              <IconButton size="small">
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              mb={2}
              sx={{ color: reduxTheme.primary_colors[100], fontWeight: "bold" }}
            >
              {item.title}
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <img
                src={item.avatar || thumbnailTemplate}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Avatar src={item.avatar} sx={{ width: 20, height: 20 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  {item.author}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Comment fontSize="small" color="action" />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  {item.comments} Comments
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

// Column Component
const KanbanColumn: React.FC<{ column: IColumn }> = ({ column }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  return (
    <Paper
      sx={{
        p: 2,
        minheight: "100vh",
        boxShadow: 0,
        width: "100%",
        minWidth: 300,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: reduxTheme.primary_colors[400],
          }}
        />
        <Typography variant="h6" fontWeight="medium">
          {column.title}
        </Typography>
        <Chip
          label={column.count}
          size="small"
          sx={{
            backgroundColor: reduxTheme.primary_colors[400],
            color: reduxTheme.primary_colors[900],
            minWidth: 24,
          }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {column.description}
      </Typography>
      <SortableContext
        items={column.items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <AnimatePresence>
          {column.items.map((item) => (
            <SortableCard key={item.id} item={item} />
          ))}
        </AnimatePresence>
      </SortableContext>
    </Paper>
  );
};

// Main Component
const MemoryTableListing: React.FC = () => {
  const [columns, setColumns] = useState<any[]>(initialColumns);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const defferedSearchTerm = useDeferredValue(appliedSearchTerm);
  const [tableViewStreamInfo, setTableViewStreamsInfo] = useState<
    MemoryBookStreamInfo[]
  >([]);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [isLoading, setIsLoading] = useState(true);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [rowCount, setRowCount] = useState(0);
  const dispatch = useDispatch();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  useEffect(() => {
    fetchStreams();
  }, [paginationModel, dispatch, defferedSearchTerm]);

  const fetchStreams = async () => {
    setIsLoading(true);
    let allMemoryBookStreams: MemoryBookStreamInfo[] = [];

    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: MemoryBookStreamInfo[];
      }>("/api/streams/stream/memory-book/", params);
      setIsLoading(false);

      allMemoryBookStreams = [
        ...allMemoryBookStreams,
        ...response.data.results,
      ];
      setRowCount(response.data.count);
      setTableViewStreamsInfo(allMemoryBookStreams);

      // Group streams by status
      const groupedColumns = initialColumns.map((col) => {
        const items = allMemoryBookStreams
          .filter((stream) => {
            const mappedStatus =
              statusToColumnMap[getBookID(stream.is_delivered)] || "other";
            return mappedStatus === col.id;
          })
          .map((stream) => ({
            id: stream.stream_id,
            title: stream.name_of_deceased,
            /*  priority:
              stream.priority && stream.priority.toLowerCase() === "high"
                ? ("High" as const)
                : ("Low" as const), */
            //author: stream.author || "Jane Smith",
            comments: stream.total_comments,
            avatar: "/api/placeholder/32/32",
          }));
        return {
          ...col,
          items,
          count: items.length,
        };
      });

      setColumns(groupedColumns);
    } catch (err: any) {
      showErrorToast("Failed to load streams. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const searchValue = e.target.value;

    /* if (searchValue.length <= 3) {
      return;
    }  */

    setAppliedSearchTerm(searchValue);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <MemoryBookStats />
      <Grid container>
        <Box
          sx={{
            backgroundColor: reduxTheme.primary_colors[900],
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            width: "25%",
            mb: 2,
          }}
        >
          <SearchOutlined sx={{ mr: 1 }} />
          <InputBase
            placeholder="Search by title, service, or package..."
            onChange={(e) => updateSearchTerm(e)}
            sx={{ ml: 1, flex: 1 }}
          />
          <IconButton>
            <FilterListOutlined />
          </IconButton>
        </Box>

        <Box sx={{ ml: 2 }}>
          <Button
            variant="contained"
            sx={{
              mr: 1,
              bgcolor:
                viewMode === "table"
                  ? "primary"
                  : reduxTheme.primary_colors[800],
            }}
            onClick={() => setViewMode("table")}
          >
            <ViewHeadlineIcon
              sx={{
                color:
                  viewMode === "table"
                    ? reduxTheme.primary_colors[900]
                    : reduxTheme.primary_colors[700],
              }}
            />
          </Button>
          <Button
            variant="contained"
            onClick={() => setViewMode("grid")}
            sx={{
              bgcolor:
                viewMode === "grid"
                  ? "primary"
                  : reduxTheme.primary_colors[800],
            }}
          >
            <CalendarViewWeekIcon
              sx={{
                color:
                  viewMode === "grid"
                    ? reduxTheme.primary_colors[900]
                    : reduxTheme.primary_colors[700],
              }}
            />
          </Button>
        </Box>
      </Grid>

      {viewMode === "grid" && (
        <DndContext
          //sensors={sensors}
          collisionDetection={closestCenter}
          //onDragEnd={handleDragEnd}
        >
          <Box display="flex" gap={2} sx={{ overflowX: "auto", pb: 2 }}>
            {columns.map((column) => (
              <motion.div
                key={column.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <KanbanColumn column={column} />
              </motion.div>
            ))}
          </Box>
        </DndContext>
      )}

      {viewMode === "table" && (
        <TableView
          tableViewStreams={tableViewStreamInfo}
          isLoading={isLoading}
          paginationModel={paginationModel}
          rowCount={rowCount}
          handlePaginationModelChange={handlePaginationModelChange}
        />
      )}
    </Box>
  );
};

export default MemoryTableListing;
